import React from 'react'
import { motion } from 'framer-motion'
import { useI18n } from '../../../i18n/I18nProvider'

interface ScaleQuestionProps {
  question: string
  value: number | null
  onChange: (value: number) => void
  minLabel?: string
  maxLabel?: string
}

export function ScaleQuestion({
  question,
  value,
  onChange,
  minLabel,
  maxLabel,
}: ScaleQuestionProps) {
  const { t } = useI18n()

  const effectiveMin = minLabel ?? t('common.stronglyDisagree')
  const effectiveMax = maxLabel ?? t('common.stronglyAgree')

  // Only split when explicitly requested via "|" or "\n"
  const splitLabel = (label: string) => {
    if (label.includes('|')) return label.split('|')
    if (label.includes('\n')) return label.split('\n')
    return [label]
  }

  const minLines = splitLabel(effectiveMin)
  const maxLines = splitLabel(effectiveMax)

  return (
    <div className="space-y-12">
      <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] leading-tight tracking-tight text-center max-w-3xl mx-auto">
        {question}
      </h3>

      <div className="space-y-6">
        <div className="flex justify-center items-center gap-1.5 sm:gap-6 max-w-3xl mx-auto">
          {[1, 2, 3, 4, 5, 6, 7].map((num) => {
            const isSelected = value === num
            return (
              <motion.button
                key={num}
                type="button"
                onClick={() => onChange(num)}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  duration: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                className={`
                  relative w-11 h-11 sm:w-16 sm:h-16 rounded-full flex items-center justify-center
                  text-base sm:text-xl font-medium transition-all duration-500 shadow-sm flex-shrink-0
                  ${
                    isSelected
                      ? 'gradient-button text-white organic-shadow'
                      : 'bg-white border border-[#E8E4EE] text-[#6B6575] hover:border-[#B5A8D4] hover:text-[#9B87C7] hover:shadow-md'
                  }
                `}
              >
                <span className="relative z-10">{num}</span>
              </motion.button>
            )
          })}
        </div>

        <div className="flex justify-between text-xs sm:text-sm font-medium text-[#9B95A3] px-2 sm:px-4 uppercase tracking-wider max-w-3xl mx-auto opacity-70">
          <span className="text-left flex flex-col leading-tight max-w-[40%] whitespace-pre-line">
            {minLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </span>

          <span className="text-right flex flex-col leading-tight max-w-[40%] whitespace-pre-line">
            {maxLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </span>
        </div>
      </div>
    </div>
  )
}